import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Plus, Trash2, Save, Loader2, Check, AlertTriangle, FlaskConical } from 'lucide-react';

const sendTestFn = httpsCallable(functions, 'adminEmailSendTest');

// Default reminder EMAIL CONTENT stays in Malay — these go to customers.
const DEFAULT_STEPS = [
    { id: 'd7', dayOffset: -7, subject: '🔔 Lesen SESAAT anda tamat dalam 7 hari', bodyText: 'Hai!\n\nLesen Pakej Premium SESAAT anda akan tamat **dalam 7 hari**. Renew sekarang supaya akses anda tak terputus.\n\n[Renew Sekarang](https://app.sesaat.ai/pricing)' },
    { id: 'd1', dayOffset: -1, subject: '⚠️ TERAKHIR — Lesen SESAAT anda tamat ESOK', bodyText: 'Peringatan terakhir!\n\nLesen Premium SESAAT anda tamat **ESOK**. Renew sekarang untuk elak gangguan akses.\n\n[Renew Sekarang](https://app.sesaat.ai/pricing)' },
];

function offsetToUi(dayOffset) {
    const o = Number(dayOffset) || 0;
    if (o === 0) return { when: 'on', days: 0 };
    if (o < 0) return { when: 'before', days: -o };
    return { when: 'after', days: o };
}
function uiToOffset(when, days) {
    const d = Math.abs(Number(days) || 0);
    if (when === 'before') return -d;
    if (when === 'after') return d;
    return 0;
}
function newStep() {
    return { id: 's' + Math.random().toString(36).slice(2, 8), when: 'before', days: 3, subject: '', bodyText: '' };
}

export default function RenewalSequenceEditor() {
    const [enabled, setEnabled] = useState(true);
    const [steps, setSteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const snap = await getDoc(doc(db, 'email_settings', 'renewal_sequence'));
            const src = snap.exists() && Array.isArray(snap.data().steps) && snap.data().steps.length
                ? { enabled: snap.data().enabled !== false, steps: snap.data().steps }
                : { enabled: true, steps: DEFAULT_STEPS };
            setEnabled(src.enabled);
            setSteps(src.steps.map(s => ({ id: s.id || newStep().id, subject: s.subject || '', bodyText: s.bodyText || '', ...offsetToUi(s.dayOffset) })));
        } catch (e) {
            console.error('[RenewalSeq] load failed:', e);
            setSteps(DEFAULT_STEPS.map(s => ({ id: s.id, subject: s.subject, bodyText: s.bodyText, ...offsetToUi(s.dayOffset) })));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const updateStep = (idx, patch) => setSteps(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));
    const addStep = () => setSteps(prev => [...prev, newStep()]);

    const handleSave = async () => {
        setBusy('save'); setMsg(null);
        try {
            const payloadSteps = steps.map(s => ({
                id: s.id,
                dayOffset: uiToOffset(s.when, s.days),
                subject: s.subject,
                bodyText: s.bodyText,
            }));
            await setDoc(doc(db, 'email_settings', 'renewal_sequence'), {
                enabled,
                steps: payloadSteps,
                updatedAt: serverTimestamp(),
            }, { merge: true });
            setMsg({ type: 'success', text: 'Reminder schedule saved. The system will follow this schedule every day.' });
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to save: ' + e.message });
        } finally { setBusy(''); }
    };

    const handleTest = async (s) => {
        if (!s.subject || !s.bodyText) { setMsg({ type: 'error', text: 'Fill in the subject & message first.' }); return; }
        setBusy('test-' + s.id); setMsg(null);
        try {
            const r = await sendTestFn({ subject: s.subject, bodyText: s.bodyText });
            setMsg({ type: 'success', text: r.data.dryRun ? `Practice mode: test prepared (no real email sent).` : `Test email sent to ${r.data.to}.` });
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally { setBusy(''); }
    };

    if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>;

    return (
        <div className="max-w-3xl space-y-5">
            {/* Intro — tajuk kini dipapar oleh kepala seksyen EmailBlastTab */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                <p className="text-sm text-slate-600 dark:text-slate-300">Set when to remind users — e.g. 7 days before expiry, 1 day before, or 3 days after expiry (win-back). Each step can have a different message.</p>
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 accent-violet-600" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Enable automatic renewal reminders</span>
                </label>
            </div>

            {msg && (
                <div className={`flex items-start gap-2 p-3 rounded-xl text-sm ${msg.type === 'error' ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'}`}>
                    {msg.type === 'error' ? <AlertTriangle size={16} className="mt-0.5" /> : <Check size={16} className="mt-0.5" />}
                    <span>{msg.text}</span>
                </div>
            )}

            {/* Steps */}
            {steps.map((s, idx) => (
                <div key={s.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400 uppercase">Step {idx + 1}</span>
                        <button onClick={() => removeStep(idx)} className="text-slate-400 hover:text-rose-500"><Trash2 size={16} /></button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-slate-500">Send</span>
                        {s.when !== 'on' && (
                            <input type="number" min={0} max={90} value={s.days} onChange={e => updateStep(idx, { days: e.target.value })}
                                className="w-20 p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 outline-none" />
                        )}
                        {s.when !== 'on' && <span className="text-slate-500">days</span>}
                        <select value={s.when} onChange={e => updateStep(idx, { when: e.target.value })}
                            className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 outline-none">
                            <option value="before">before expiry</option>
                            <option value="on">on expiry day</option>
                            <option value="after">after expiry (win-back)</option>
                        </select>
                    </div>
                    <input type="text" value={s.subject} onChange={e => updateStep(idx, { subject: e.target.value })} placeholder="Email subject"
                        className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-violet-500" />
                    <textarea value={s.bodyText} onChange={e => updateStep(idx, { bodyText: e.target.value })} rows={4} placeholder="Message body (you can use **bold** and [links](https://...))"
                        className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none resize-none font-mono focus:ring-2 focus:ring-violet-500" />
                    <button onClick={() => handleTest(s)} disabled={!!busy}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50">
                        {busy === 'test-' + s.id ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                        Send test to me
                    </button>
                    {s.when === 'after' && <p className="text-[11px] text-amber-500">Note: emails after expiry count as promotional — an "unsubscribe" button will be added automatically.</p>}
                </div>
            ))}

            <div className="flex items-center gap-2">
                <button onClick={addStep} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700">
                    <Plus size={16} /> Add Step
                </button>
                <button onClick={handleSave} disabled={!!busy}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:brightness-110 disabled:opacity-50 ml-auto">
                    {busy === 'save' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Save Schedule
                </button>
            </div>
        </div>
    );
}
